import { observer } from 'mobx-react-lite'
import { type IDropdownItem, Box, TextFieldWithLabel } from 'shared/ui'
import { SelectField } from 'shared/ui/Select/SelectField/SelectField'
import { type IResponseEinItem } from 'entities/Compliance/api/types/complianceType'
import { type IAddressFieldsError } from 'pages/settings/ui/BusinessAddressForm/store/businessAddressFormStore'
import { WhereCanIFindBusinessInfoButton } from 'pages/settings/ui/LinkButton/WhereCanIFindBusinessInfoButton'
import styles from './styles.module.scss'

type ICountryInfo = {
  placeholder: string
  state: string
  statePlaceholder: string
  code: string
  codePlaceholder: string
}

export type IBusinessAddressFormProps = {
  addressData: IResponseEinItem | null
  countries: IDropdownItem[]
  activeCountryId: string
  activeCountryInfo: ICountryInfo
  states: IDropdownItem[]
  zipCode?: string
  zipLocalErrorMessage: string | null
  fieldsError?: IAddressFieldsError | null
  setActiveCountry: (countryId: string, clearFields: boolean) => void
  setStreet1: (street: string) => void
  setStreet2: (street: string) => void
  setCity: (city: string) => void
  setState: (state: string) => void
  setZip: (zip: string) => void
  checkZipLength: () => void
  countrySelectClassName?: string
  textInputClassName?: string
  disabled?: boolean
}

export const BusinessAddressForm = observer(
  ({
    addressData,
    countries,
    activeCountryId,
    activeCountryInfo,
    states,
    zipCode,
    zipLocalErrorMessage,
    fieldsError,
    setActiveCountry,
    setStreet1,
    setStreet2,
    setCity,
    setState,
    setZip,
    checkZipLength,
    countrySelectClassName,
    textInputClassName,
    disabled = false,
  }: IBusinessAddressFormProps) => {
    const inputClassName = textInputClassName || styles.textInput

    return (
      <>
        <SelectField
          boxWidth='100%'
          error={fieldsError?.country}
          labelProps={{
            label: {
              text: 'Country',
            },
          }}
          items={countries}
          value={activeCountryId}
          setValue={(countryId) => setActiveCountry(countryId, true)}
          btnProps={{
            size: 'large',
            className: styles.countriesSelect,
            disabled,
            tabIndex: -1,
          }}
          className={countrySelectClassName || styles.countrySelectWrap}
          dropdownProps={{
            disabled,
            textFieldProps: {
              InputProps: {
                placeholder: 'Search',
              },
            },
            tabIndex: 0,
          }}
        />

        {activeCountryId && (
          <>
            <TextFieldWithLabel
              marginBottom={3}
              marginTop={'24px'}
              errorBorder={Boolean(fieldsError?.street_address_1)}
              errorMessage={fieldsError?.street_address_1}
              labelProps={{
                label: {
                  text: 'Business address',
                  rightNode: <WhereCanIFindBusinessInfoButton />,
                },
                marginBottom: 4,
              }}
              textFieldProps={{
                className: inputClassName,
                value: addressData?.street1 || '',
                onChange: setStreet1,
                disabled,
                InputProps: {
                  placeholder: 'e.g. Acme Street 1',
                },
              }}
            />

            <TextFieldWithLabel
              marginBottom={3}
              errorBorder={Boolean(fieldsError?.street_address_2)}
              errorMessage={fieldsError?.street_address_2}
              labelProps={{
                label: {
                  text: 'Business address 2 (optional)',
                },
              }}
              textFieldProps={{
                className: inputClassName,
                value: addressData?.street2 || '',
                onChange: setStreet2,
                disabled,
                InputProps: {
                  placeholder: 'e.g. Building name',
                },
              }}
            />

            <TextFieldWithLabel
              marginBottom={3}
              errorBorder={Boolean(fieldsError?.city)}
              errorMessage={fieldsError?.city}
              labelProps={{
                label: {
                  text: 'City',
                },
              }}
              textFieldProps={{
                className: inputClassName,
                value: addressData?.city || '',
                onChange: setCity,
                disabled,
                InputProps: {
                  placeholder: activeCountryInfo.placeholder,
                },
              }}
            />

            <Box display='flex' justifyContent='space-between' gap={'12px'}>
              <Box flex={1} marginBottom={'0px'}>
                {activeCountryId === 'US' || activeCountryId === 'CA' ? (
                  <SelectField
                    value={addressData?.state}
                    items={states}
                    setValue={setState}
                    error={fieldsError?.state_id}
                    btnProps={{
                      size: 'large',
                      disabled,
                      style: {
                        height: '40px',
                        color: !addressData?.state ? 'var(--content-neutral-primary)' : undefined,
                      },
                      tabIndex: -1,
                    }}
                    dropdownProps={{
                      disabled,
                      textFieldProps: {
                        InputProps: {
                          placeholder: 'Search',
                        },
                      },
                      tabIndex: 0,
                    }}
                    labelProps={{
                      label: {
                        text: activeCountryInfo.state,
                      },
                    }}
                  />
                ) : (
                  <TextFieldWithLabel
                    errorBorder={Boolean(fieldsError?.state_id)}
                    errorMessage={fieldsError?.state_id}
                    labelProps={{
                      label: {
                        text: activeCountryInfo.state,
                      },
                    }}
                    textFieldProps={{
                      className: inputClassName,
                      value: addressData?.state || '',
                      onChange: setState,
                      disabled,
                      InputProps: {
                        placeholder: activeCountryInfo.statePlaceholder,
                      },
                    }}
                  />
                )}
              </Box>
              <Box flex={1} marginBottom={'0px'}>
                <TextFieldWithLabel
                  errorBorder={
                    Boolean(fieldsError?.zip_code) ||
                    Boolean(fieldsError?.postal_code) ||
                    Boolean(zipLocalErrorMessage)
                  }
                  errorMessage={
                    zipLocalErrorMessage ||
                    fieldsError?.postal_code ||
                    fieldsError?.zip_code ||
                    undefined
                  }
                  labelProps={{
                    label: {
                      text: activeCountryInfo.code,
                    },
                  }}
                  textFieldProps={{
                    value: zipCode,
                    className: styles.zipInput,
                    onChange: setZip,
                    onBlur: checkZipLength,
                    disabled,
                    InputProps: {
                      placeholder: activeCountryInfo.codePlaceholder,
                    },
                  }}
                />
              </Box>
            </Box>
          </>
        )}
      </>
    )
  }
)
